import Slugger from 'github-slugger'
import Script from 'next/script'
import { ReactNode } from 'react'
import { TbChevronDown } from 'react-icons/tb'
import { Markdown } from 'routes/components/Shared'
import classes from './Accordion.module.scss'

export interface FAQAccordionItem {
  question: string
  answer: string
}

interface FAQAccordionProps {
  array: FAQAccordionItem[]
}

interface FAQAccordionItemProps extends FAQAccordionItem {
  children?: ReactNode
}

interface FAQAccordionWrapperProps {
  children: ReactNode
}

// Main Accordion Component
export const FAQAccordion = ({ array }: FAQAccordionProps) => {
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: array.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  }

  return (
    <>
      <FAQAccordionWrapper>
        {array.map((item, index) => (
          <FAQAccordionItem
            key={`faq-${index}`}
            question={item.question}
            answer={item.answer}
          >
            <Markdown>{item.answer}</Markdown>
          </FAQAccordionItem>
        ))}
      </FAQAccordionWrapper>
      <Script
        id='faq-schema'
        type='application/ld+json'
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(faqSchema, null, 2),
        }}
      />
    </>
  )
}

export function FAQAccordionWrapper({ children }: FAQAccordionWrapperProps) {
  return (
    <div
      className={classes.wrapper}
      itemScope
      itemType='https://schema.org/FAQPage'
    >
      <ul className={classes.list}>{children}</ul>
    </div>
  )
}

export function FAQAccordionItem({
  question,
  children,
}: FAQAccordionItemProps) {
  const slugger = new Slugger()
  const index = slugger.slug(question)

  return (
    <li
      className={classes.item}
      itemScope
      itemProp='mainEntity'
      itemType='https://schema.org/Question'
    >
      <input
        className={classes.checkbox}
        id={`check-${index}`}
        type='checkbox'
      />

      <label
        className={classes.label}
        htmlFor={`check-${index}`}
        itemProp='name'
      >
        {question}
        <TbChevronDown className={classes.icon} />
      </label>

      <div
        className={classes.content}
        itemScope
        itemProp='acceptedAnswer'
        itemType='https://schema.org/Answer'
      >
        <div itemProp='text'>{children}</div>
      </div>
    </li>
  )
}
